import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  AssessmentOutlined,
  BookmarkBorderRounded,
  DashboardOutlined,
  HelpOutlineRounded,
  MenuBookOutlined,
  MicNoneRounded,
  RouteOutlined,
  SettingsOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { Box, BottomNavigation, BottomNavigationAction, List, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from "@mui/material";

const primaryItems = [
  { label: "Dashboard", path: "/dashboard", icon: DashboardOutlined },
  { label: "Live Interview", path: "/interview-config", icon: MicNoneRounded },
  { label: "Reports", path: "/analytics", icon: AssessmentOutlined },
  { label: "Interview Tracks", path: "/tracks", icon: MenuBookOutlined },
  { label: "Roadmap", path: "/roadmap", icon: RouteOutlined },
  { label: "Bookmarks", path: "/dashboard", icon: BookmarkBorderRounded, selectable: false },
];

const secondaryItems = [
  { label: "Settings", path: "/dashboard", icon: SettingsOutlined, selectable: false },
  { label: "Help & Support", path: "/dashboard", icon: HelpOutlineRounded, selectable: false },
];

function Brand() {
  return (
    <Stack component={Link} to="/dashboard" direction="row" alignItems="center" spacing={1} sx={{ color: "text.primary", textDecoration: "none", px: 1 }}>
      <VerifiedUserOutlined sx={{ color: "primary.main", fontSize: 28 }} />
      <Typography sx={{ fontWeight: 700, fontSize: 16, whiteSpace: "nowrap" }}>Evaluate Yourself</Typography>
    </Stack>
  );
}

function NavList({ items }) {
  const { pathname } = useLocation();
  return (
    <List disablePadding sx={{ display: "grid", gap: 0.5 }}>
      {items.map(({ label, path, icon: Icon, selectable = true }) => {
        const selected = selectable && (pathname === path || (path === "/tracks" && pathname === "/interviews") || (label === "Reports" && pathname.startsWith("/report/")));
        return (
          <ListItemButton key={label} component={Link} to={path} selected={selected} sx={{ minHeight: 44, borderRadius: 1, px: 1.5, color: selected ? "primary.main" : "text.secondary", "&.Mui-selected": { backgroundColor: "#F5F5F5" }, "&.Mui-selected:hover": { backgroundColor: "#F5F5F5" } }}>
            <ListItemIcon sx={{ minWidth: 30, color: "inherit" }}><Icon sx={{ fontSize: 14 }} /></ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13, fontWeight: selected ? 600 : 500 }} />
          </ListItemButton>
        );
      })}
    </List>
  );
}

export default function ApplicationShell() {
  const { pathname } = useLocation();
  const mobileItems = primaryItems.slice(0, 5);
  const mobileValue = Math.max(0, mobileItems.findIndex((item) => pathname === item.path || (item.path === "/tracks" && pathname === "/interviews")));

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default", display: "flex" }}>
      <Box component="aside" sx={{ display: { xs: "none", md: "flex" }, position: "fixed", inset: "0 auto 0 0", width: 248, bgcolor: "background.paper", borderRight: "1px solid", borderColor: "divider", p: 2.5, flexDirection: "column", zIndex: 10 }}>
        <Box sx={{ py: 1.5, mb: 2 }}><Brand /></Box>
        <NavList items={primaryItems} />
        <Box sx={{ mt: "auto" }}><NavList items={secondaryItems} /></Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, ml: { md: "248px" }, pb: { xs: 9, md: 0 } }}>
        <Box component="header" sx={{ display: { xs: "flex", md: "none" }, height: 72, px: 2.5, alignItems: "center", bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", position: "sticky", top: 0, zIndex: 9 }}><Brand /></Box>
        <Box component="main" sx={{ minHeight: { xs: "calc(100dvh - 72px)", md: "100dvh" } }}><Outlet /></Box>
      </Box>

      <Paper elevation={0} sx={{ display: { xs: "block", md: "none" }, position: "fixed", inset: "auto 0 0", zIndex: 20, borderTop: "1px solid", borderColor: "divider", borderRadius: 0 }}>
        <BottomNavigation showLabels value={mobileValue} sx={{ height: 72 }}>
          {mobileItems.map(({ label, path, icon: Icon }) => <BottomNavigationAction key={label} component={Link} to={path} label={label === "Live Interview" ? "Interview" : label} icon={<Icon sx={{ fontSize: 14 }} />} sx={{ minWidth: 0, px: 0.5, "& .MuiBottomNavigationAction-label": { fontSize: 12, mt: 0.5 } }} />)}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
